<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { type RouteLocationRaw } from 'vue-router';

import Breadcrumbs from './Breadcrumbs.vue';
import BackButton from './BackButton.vue';

import type { BreadcrumbElement } from '@/helpers/breadcrumbs.helper';
import type { Locales } from '@/services/types/api.types';

// CONST
const { t } = useI18n();

// PROPS
const props = defineProps<{
  breadcrumbs: BreadcrumbElement[];
  to: RouteLocationRaw;
  languageEnabled: Locales[];
  // Why there is nothing to preview, when there is not: the button is then greyed out and says why.
  previewDisabledReason?: string;
}>();

// EMITS
const emit = defineEmits<{
  (e: 'preview'): void;
}>();
</script>
<template>
  <div class="navigation">
    <div class="navigation__breadcrumbs">
      <breadcrumbs :elements="breadcrumbs" :language-enabled="languageEnabled" />
    </div>
    <div class="navigation__tabs">
      <slot name="top-navigation"></slot>
    </div>
    <div class="navigation__actions">
      <back-button :to="to" />
      <!-- a disabled button gets no hover, so the tooltip of a refused preview sits on its wrapper -->
      <span>
        <q-btn
          color="primary"
          :label="t('global.label.preview')"
          :disable="!!props.previewDisabledReason"
          :aria-label="
            props.previewDisabledReason
              ? t('global.action-blocked', { action: t('global.label.preview'), reason: props.previewDisabledReason })
              : undefined
          "
          @click="emit('preview')"
        />
        <q-tooltip v-if="props.previewDisabledReason">{{ props.previewDisabledReason }}</q-tooltip>
      </span>
      <slot name="actions"></slot>
    </div>
  </div>
</template>
<style lang="scss">
.navigation {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-left: 10px;
  height: 36px;

  &__breadcrumbs {
    width: 35%;
    height: inherit;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
  }

  &__tabs {
    width: 30%;
    height: inherit;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }

  &__actions {
    width: 35%;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: $space-md;
  }
}
@media (max-width: 1400px) {
  .navigation {
    height: 80px;

    &__breadcrumbs {
      width: 20%;

      .q-gutter-sm > .items-center .q-breadcrumbs__el {
        height: 24px;
      }
    }

    &__tabs {
      width: 40%;

      .consultation-navigation-tabs > .dynamic-tabs {
        display: flex;
        align-items: center;
      }
    }

    &__actions {
      width: 35%;
    }
  }
}
</style>
